import React from 'react';
import { 
  Globe, 
  Bot, 
  MessageCircle, 
  Star, 
  FileText, 
  Workflow,
  Sparkles
} from 'lucide-react';

const SolutionsSection = () => {
  const solutions = [
    {
      icon: Globe,
      title: 'AI Website Creation',
      description: 'Modern AI-assisted websites built quickly and optimized for conversions. Fast, mobile-friendly, and designed to turn visitors into customers.',
      color: 'blue',
    },
    {
      icon: Bot,
      title: 'AI Chatbot Integration',
      description: '24/7 chatbot that answers customer questions and captures leads automatically. Never miss another inquiry, even after hours.',
      color: 'violet',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Lead Automation',
      description: 'Turn website visitors into WhatsApp conversations and potential customers. Instant connection with leads when interest is highest.',
      color: 'green',
    },
    {
      icon: Star,
      title: 'Review Automation',
      description: 'Automatically request Google reviews from customers to increase local search rankings and build trust with new customers.',
      color: 'amber',
    },
    {
      icon: FileText,
      title: 'AI SEO Content Updates',
      description: 'Monthly AI-generated content and SEO improvements to help businesses rank higher on Google and attract more organic traffic.',
      color: 'cyan',
    },
    {
      icon: Workflow,
      title: 'Business Automation',
      description: 'Automate repetitive tasks such as lead capture, follow-ups, and customer notifications. Save time and never drop the ball.',
      color: 'rose',
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
      violet: 'bg-violet-50 text-violet-600 group-hover:bg-violet-100',
      green: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100',
      amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-100',
      cyan: 'bg-cyan-50 text-cyan-600 group-hover:bg-cyan-100',
      rose: 'bg-rose-50 text-rose-600 group-hover:bg-rose-100',
    };
    return colors[color] || colors.blue;
  };

  return (
    <section 
      id="solutions"
      className="py-24 lg:py-32 bg-white"
      data-testid="solutions-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">AI Growth Solutions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Supercharge Your Business with{' '}
            <span className="gradient-text">AI Technology</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            We don't just build websites. We create AI-powered growth systems that help local businesses attract more customers, automate engagement, and increase revenue.
          </p>
        </div>

        {/* Solutions Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="group service-card bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
              data-testid={`solution-card-${index}`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors ${getColorClasses(solution.color)}`}>
                <solution.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                {solution.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {solution.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
